/**
 * "Instalar na tela de início" — o convite, e quando ele não deve aparecer.
 *
 * O que o navegador entrega é assimétrico, e é essa assimetria que desenha a
 * tela:
 *
 *  - **Rodando instalado** dá para saber em todo lugar: `display-mode:
 *    standalone` (e o `navigator.standalone` do iOS antigo).
 *  - **Instalado, mas visto de dentro do navegador**, o iPhone não conta. Não
 *    há API, e desde o iOS 16.4 o app da tela de início tem armazenamento
 *    próprio — nem uma marca em `localStorage` atravessa de volta para o
 *    Safari. E não precisa contar: quem tem o app na tela de início abre por
 *    ele, não pelo navegador. Por isso o convite não tem botão de fechar — ele
 *    some sozinho, no dia em que a pessoa passa a entrar pelo app.
 *
 * O Chrome é o único que oferece o diálogo de verdade, e só depois de
 * considerar o app instalável — manifesto com ícone rasterizado de 192px e um
 * service worker com handler de `fetch`. Como o service worker só é registrado
 * em produção (ver `main.tsx`), o botão nativo **não aparece em `pnpm dev`**:
 * para vê-lo é preciso o build servido por HTTPS.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';

/** O evento do Chromium; ainda não está no lib.dom do TypeScript. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallPlatform = 'installed' | 'ios' | 'webview' | 'other';

export interface InstallEnvironment {
  userAgent: string;
  /** Já está rodando fora do navegador. */
  standalone: boolean;
  /** Separa o iPad — que se apresenta como Macintosh — de um Mac de verdade. */
  maxTouchPoints: number;
}

/**
 * Navegador embutido de rede social.
 *
 * Este ramo não é preciosismo: a home da barbearia é divulgada na bio do
 * Instagram, e dentro do navegador embutido dele **não existe instalar** — não
 * há botão de compartilhar do Safari nem prompt do Chrome. Sem separar este
 * caso, o cliente segue a instrução do iPhone, não acha o botão que a tela
 * mandou procurar e conclui que o app é quebrado.
 */
const IN_APP_BROWSERS = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Line\/|MicroMessenger|TikTok/i;

const IOS_DEVICES = /iPad|iPhone|iPod/;

export function detectPlatform(env: InstallEnvironment): InstallPlatform {
  if (env.standalone) return 'installed';
  if (IN_APP_BROWSERS.test(env.userAgent)) return 'webview';

  // O iPad se declara "Macintosh" desde o iPadOS 13; o que o denuncia é ter
  // toque. Sem esta segunda condição, todo Mac veria instruções de iPhone.
  const isIos =
    IOS_DEVICES.test(env.userAgent) || (/Macintosh/.test(env.userAgent) && env.maxTouchPoints > 1);

  return isIos ? 'ios' : 'other';
}

export function readEnvironment(): InstallEnvironment {
  return {
    userAgent: navigator.userAgent,
    standalone:
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true,
    maxTouchPoints: navigator.maxTouchPoints,
  };
}

// ── Estado, guardado antes de o React existir ────────────────────────────────

interface InstallState {
  /** O Chromium ofereceu o diálogo e ele está guardado aqui. */
  canPrompt: boolean;
  installed: boolean;
}

let deferred: BeforeInstallPromptEvent | null = null;

// A referência precisa ser estável entre leituras: `useSyncExternalStore`
// compara por identidade e entraria em laço infinito com um objeto novo a cada
// chamada.
let state: InstallState = { canPrompt: false, installed: false };

const subscribers = new Set<() => void>();

function update(patch: Partial<InstallState>): void {
  state = { ...state, ...patch };
  for (const notify of subscribers) notify();
}

function subscribe(listener: () => void): () => void {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

/**
 * O `beforeinstallprompt` dispara logo depois do carregamento — antes de a tela
 * montar, porque o app espera a resposta de `GET /tenant` para renderizar
 * qualquer coisa. Um `addEventListener` dentro do componente chegaria tarde e o
 * botão nunca apareceria. Por isso a escuta é de módulo: acontece na avaliação
 * do bundle, e o componente só lê o que já foi guardado.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Sem isto o Chrome mostra a própria barra, no topo, longe do polegar.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    update({ canPrompt: true });
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    update({ canPrompt: false, installed: true });
  });
}

// ── O que a tela consome ────────────────────────────────────────────────────

export type InstallStatus = 'hidden' | 'prompt' | 'ios' | 'webview';

export interface InstallInvite {
  status: InstallStatus;
  /** Abre o diálogo do Chromium. Só existe em `status === 'prompt'`. */
  install: () => Promise<void>;
}

export function useInstallPrompt(): InstallInvite {
  const store = useSyncExternalStore(subscribe, () => state);
  const platform = useMemo(() => detectPlatform(readEnvironment()), []);

  const install = useCallback(async () => {
    const event = deferred;
    if (!event) return;

    // O evento vale uma vez só: aceito ou recusado, ele é descartado. O botão
    // volta na próxima visita, quando o Chrome oferecer o diálogo de novo —
    // insistir dentro da mesma sessão, depois de um "não", é o que faz alguém
    // desinstalar antes de usar.
    deferred = null;
    update({ canPrompt: false });

    await event.prompt();
    await event.userChoice;
  }, []);

  const status = ((): InstallStatus => {
    if (store.installed || platform === 'installed') return 'hidden';
    // Sem contexto seguro não há service worker, e sem ele não há app
    // instalado — só um atalho para uma página que não abre offline.
    if (!window.isSecureContext) return 'hidden';
    if (store.canPrompt) return 'prompt';
    if (platform === 'ios') return 'ios';
    if (platform === 'webview') return 'webview';
    // Chromium sem o evento é o caso de já estar instalado, e desktop nenhum
    // outro navegador instala: melhor calar do que ensinar o gesto errado.
    return 'hidden';
  })();

  return { status, install };
}
